import { useEffect, useMemo, useRef, useState } from "react";
import {
  CircleMarker,
  GeoJSON,
  LayersControl,
  MapContainer,
  TileLayer,
  Tooltip,
} from "react-leaflet";
import { circleMarker } from "leaflet";
import type { GeoJSON as CapaLeaflet, LatLng, LatLngBounds, Layer } from "leaflet";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { buildProblemsFor } from "../model/subBasinState.ts";
import {
  layerBounds,
  geometryBounds,
  type AquiferFeature,
  type AquiferLayer,
  type OutletPointLayer,
  type SubBasinLayer,
  type SubBasinFeature,
} from "../model/gisLayer.ts";
import type { LoadedProject } from "../model/project.ts";
import type { DailySeries } from "../model/dailySeries.ts";
import type { AemetProvenance } from "../model/storage.ts";
import type { SubBasin, EvalHidProject } from "../model/types.ts";
import { BASEMAPS } from "./basemap.ts";
import { AemetStationsLayer } from "./aemet/AemetStationsLayer.tsx";
import { MitecoSubBasinsLayer } from "./MitecoSubBasinsLayer.tsx";
import { MitecoBasinsLayer } from "./MitecoBasinsLayer.tsx";
import { MitecoBasinsOutline } from "./MitecoBasinsOutline.tsx";
import type { MapMitecoMode } from "../model/mitecoLayers.ts";
import { DrawEvents, VistaPreviaDibujo } from "./MapDrawing.tsx";
import { isPointTool, TOOL_LABEL, type DrawingTool } from "./drawingTools.ts";
import type { PanelKind } from "./editor/panelDefinitions.ts";
import { RectangleSelectionLayer, type RectangleSelectionMode } from "./RectangleSelection.tsx";
import { TooltipVisibilityFix } from "./TooltipVisibilityFix.tsx";
import { AQUIFER_STYLE, OUTLET_POINT_STYLE } from "./mapStyles.ts";
import { Legend } from "./Legend.tsx";
import { AssignmentColorLegend } from "./AssignmentColorLegend.tsx";
import { GenericLayersLegend } from "./GenericLayersLegend.tsx";
import type { GenericLayer } from "../model/genericLayer.ts";
import { buildColorMap, colorFor, type AssignmentColorMode } from "../model/assignmentColors.ts";
import { EntityEditDialogs } from "./EntityEditDialogs.tsx";
import { useEntityEditDialogs } from "./useEntityEditDialogs.ts";
import { sameSelection } from "./resultSelection.ts";
import type { MapResultsMode, MapSelectionMode, MapSeriesMode } from "./mapModes.ts";
import { escapeHtml } from "./escapeHtml.ts";
import { aquiferOf, subBasinOf } from "./subBasinMapEntities.ts";
import {
  genericLayerPathOptions,
  SELECTION_HIGHLIGHT,
  styleFor,
  visualStateOf,
} from "./subBasinMapStyle.ts";
import { onEachFeature } from "./subBasinMapOnEachFeature.ts";
import { SnowPatterns } from "./SnowPatterns.tsx";
import { GenericLayersSync } from "./GenericLayersSync.tsx";
import { PanToSubBasin } from "./PanToSubBasin.tsx";

// How long the sub-basins/aquifers GeoJSON layers wait for `signature` (see
// its own comment) to stay quiet before actually remounting — see the
// `debouncedSignature` state below for why this exists.
const SIGNATURE_DEBOUNCE_MS = 400;

/**
 * Map of an EvalHid project's sub-basins. Outside any special mode,
 * pressing a polygon/point opens its edit dialog directly (same pattern as
 * SubBasinCanvas.tsx in the card view) — no need to go through "Edit
 * project" just for that. In editing mode (`selectionMode` present, see
 * EditBar.tsx), a single click (de)selects instead (plain click toggles;
 * Shift+click adds, Ctrl+click removes) — likewise Shift+drag adds via a
 * rectangle, Ctrl+drag removes (see RectangleSelection.tsx) — to then apply
 * an action (assign zone, delete...) to the whole selection; a DOUBLE-click
 * still opens the edit dialog directly even in this mode (see
 * subBasinMapOnEachFeature.ts), same shortcut SubBasinsPanel.tsx's own
 * catalog table row offers — a plain click's toggle is deferred a few
 * hundred ms and cancelled outright if a second click follows fast enough
 * to become a dblclick, so double-clicking to edit never flashes the
 * polygon (de)selected first, and leaves selection completely untouched.
 * Outlet points/aquifers don't take part in that multi-selection, so they
 * always open their dialog on a single click (except in drawing or results
 * mode).
 */
export function SubBasinMap({
  layer,
  outletPointsLayer,
  aquifersLayer,
  project,
  series,
  selectionMode,
  drawingMode,
  resultsMode,
  seriesMode,
  editorPanel,
  onCompletarDibujo,
  onCancelDrawing,
  onChangeProject,
  onChangeSeries,
  mitecoMode,
  aemetProvenance,
  onRecordAemetProvenance,
  observedFlow,
  onChangeObservedFlow,
  calibrationMode,
  colorMode,
  genericLayers,
  onChangeGenericLayers,
  isEphemeral,
  highlightedSubBasinCode,
}: {
  layer: SubBasinLayer;
  /** Outlet points with geometry (drawn by hand, or imported — see MapDrawing.tsx). Absent/null: none are drawn. */
  outletPointsLayer?: OutletPointLayer | null;
  /** Same as `outletPointsLayer`, but for aquifers (polygonal). */
  aquifersLayer?: AquiferLayer | null;
  project: EvalHidProject;
  series: LoadedProject["series"];
  selectionMode?: MapSelectionMode;
  /** Active drawing tool (see EditBar.tsx) — null/undefined: drawing mode off. */
  drawingMode?: DrawingTool | null;
  /** Choosing which sub-basin/outlet point to view in the Results panel by clicking the map — see CalculationResults.tsx. */
  resultsMode?: MapResultsMode;
  /** Choosing which sub-basin/aquifer to filter the "Input series" charts by — see SeriesPanel.tsx. */
  seriesMode?: MapSeriesMode;
  /** Active tab of "Edit project" (EditBar.tsx) — on change, closes any individual edit dialog opened from here, see the useEffect below. */
  editorPanel?: PanelKind;
  /** Called with the finished geometry (a point from a click, or a polygon after "Finish"). */
  onCompletarDibujo?: (geometry: GeoJSON.Point | GeoJSON.Polygon) => void;
  onCancelDrawing?: () => void;
  /** Enables click-to-edit on sub-basins/outlet points/aquifers — without this, the map is read-only. */
  onChangeProject?: (project: EvalHidProject) => void;
  onChangeSeries?: (series: LoadedProject["series"]) => void;
  /** SubBasinsPanel.tsx's "Add from MITECO" mode — which reference layer to show and its working selection. Absent: neither MITECO layer renders. */
  mitecoMode?: MapMitecoMode;
  /** Which AEMET station(s) supplied each sub-basin's series so far — see AemetFetchDialog.tsx. */
  aemetProvenance?: Record<number, AemetProvenance>;
  onRecordAemetProvenance?: (subBasinCode: number, provenance: AemetProvenance) => void;
  /** Observed daily flow, one column per outlet point name — see storage.ts's own comment, edited from EditOutletPointDialog's "Observado" tab. */
  observedFlow?: DailySeries | null;
  onChangeObservedFlow?: (series: DailySeries | null) => void;
  /** CalibrationPanel.tsx open — the real "modo calibración" flag EditOutletPointDialog's "Serie observada" tab gates on (see App.tsx's State.calibrationPanelOpen). */
  calibrationMode?: boolean;
  /** Color sub-basins by whichever entity (zone/snow zone/outlet point/aquifer) they're assigned to — see assignmentColors.ts. Set only while EditBar.tsx is bulk-editing one of those (App.tsx computes it, not read from `editorPanel` here, since "zones" alone doesn't say which of its two tables is active). */
  colorMode?: AssignmentColorMode | null;
  /** Purely visual reference layers (points/lines/polygons), shown as toggleable entries in the map's own layers menu — see genericLayer.ts/GenericLayersPanel.tsx. Absent/empty: no extra entries. */
  genericLayers?: GenericLayer[];
  /** Reflects a generic layer's checkbox being toggled directly from the map's layers menu back into app state — see GenericLayersSync above. Absent: the checkbox still works (Leaflet keeps its own state), just not synced back. */
  onChangeGenericLayers?: (layers: GenericLayer[]) => void;
  /** App.tsx's `state.ephemeral` — whether ExampleBanner.tsx's bottom-centered banner is also on screen, see AssignmentColorLegend.tsx's `aboveEphemeralBanner` prop for why this needs to be threaded down here. */
  isEphemeral?: boolean;
  /** SubBasinSearch.tsx's last pick (App.tsx's `highlightedSubBasin`, plain UI state, see usePanels.ts) — highlighted the same way as `editingSubBasin`/`resultsMode`, and pans the map to it (PanToSubBasin, below). Absent/null: nothing highlighted. */
  highlightedSubBasinCode?: number | null;
}) {
  const t = useT();
  const language = useLanguage();
  const problemsOf = buildProblemsFor(t, series, project);
  const colorMap = colorMode ? buildColorMap(colorMode.entities) : undefined;
  const [vertices, setVertices] = useState<LatLng[]>([]);
  const {
    editingSubBasin,
    setEditingSubBasin,
    editingSubBasinObj,
    editingOutlet,
    setEditingOutlet,
    editingOutletObj,
    editingAquifer,
    setEditingAquifer,
    editingAquiferObj,
  } = useEntityEditDialogs(project);

  // Same reason as in SubBasinCanvas.tsx: an individual edit dialog opened
  // from the map is independent of "Edit project" (EditBar), which doesn't
  // unmount this component on tab change.
  useEffect(() => {
    setEditingSubBasin(null);
    setEditingOutlet(null);
    setEditingAquifer(null);
  }, [editorPanel, setEditingSubBasin, setEditingOutlet, setEditingAquifer]);

  // See the comment in onEachFeature: keeps the click handler (fixed at
  // layer mount) always reading the most recent selection.
  const selectionRef = useRef(selectionMode?.selection);
  selectionRef.current = selectionMode?.selection;

  // Same problem as selectionRef, but for mouseover/mouseout: those
  // handlers (fixed at layer mount/remount, see onEachFeature) can't
  // compute "is it selected?" once and cache it — only editingSubBasin/
  // resultsMode still force a remount on change, selectionMode.selection
  // no longer does (for performance, see the comment next to `signature`).
  // Without this, hovering over a recently (de)selected sub-basin would
  // paint it with the highlight from BEFORE the last selection change —
  // the internal state was correct, only the hover was lying.
  const isSelectedRef = useRef<(subBasin: SubBasin | null) => boolean>(() => false);
  isSelectedRef.current = (subBasin) =>
    !!(
      (subBasin && selectionMode?.selection.has(subBasin.subBasinCode)) ||
      (subBasin &&
        resultsMode &&
        sameSelection(resultsMode.selection, {
          level: "subBasin",
          id: subBasin.subBasinCode,
        })) ||
      (subBasin &&
        seriesMode?.selection?.kind === "subBasin" &&
        seriesMode.selection.id === subBasin.subBasinCode) ||
      (subBasin && subBasin.subBasinCode === editingSubBasin) ||
      (subBasin && subBasin.subBasinCode === highlightedSubBasinCode)
    );

  // Reference to the sub-basins layer to restyle it without remounting (see
  // the effect below and the comment next to `signature`).
  const subBasinsLayerRef = useRef<CapaLeaflet | null>(null);

  // Each generic layer's own underlying Leaflet layer, keyed by id — lets
  // GenericLayersSync above match a raw `overlayadd`/`overlayremove` event
  // back to a specific GenericLayer.
  const genericLayerRefs = useRef<Map<string, CapaLeaflet>>(new Map());
  // Memoized so it's referentially stable whenever `genericLayers` itself
  // doesn't change — without this, the imperative style-sync effect below
  // (deliberately keyed on this array, not on `genericLayersSignature`)
  // would re-run every render instead of only when a layer's data changes.
  const genericLayersList = useMemo(() => genericLayers ?? [], [genericLayers]);
  // Forces the whole LayersControl to remount when a layer is added/renamed/
  // removed — same reasoning as `signature` below for the sub-basins layer:
  // Leaflet's own control is plain Leaflet under the hood, its overlay
  // entries (name, which ones exist) are fixed at mount. Deliberately
  // excludes `visible`/`style`: toggling a checkbox or tweaking a color
  // shouldn't remount the whole control (see GenericLayersSync for
  // visibility; style updates get their own imperative sync once
  // GenericLayersPanel.tsx grows style controls).
  const genericLayersSignature = genericLayersList.map((l) => `${l.id}:${l.name}`).join("|");

  // Re-applies each generic layer's current style imperatively (GeoJSON's
  // `style`/`pointToLayer` props, like the sub-basins layer's own `style`
  // above, aren't re-run on prop changes alone) — so GenericLayersPanel.tsx's
  // color/weight/radius/fillOpacity controls take effect live, without the
  // full-remount `genericLayersSignature` forces for add/rename/delete
  // (which would flicker every layer and close the open layers-menu popup
  // just from dragging one color picker).
  useEffect(() => {
    for (const gl of genericLayersList) {
      genericLayerRefs.current.get(gl.id)?.setStyle(genericLayerPathOptions(gl.style));
    }
  }, [genericLayersList]);

  // Switching tools (or turning off drawing mode) discards any polygon
  // half-drawn — there'd be no point keeping it for something else.
  useEffect(() => {
    setVertices([]);
  }, [drawingMode]);

  function onDrawMapClick(latlng: LatLng) {
    if (!drawingMode) return;
    if (isPointTool(drawingMode)) {
      onCompletarDibujo?.({ type: "Point", coordinates: [latlng.lng, latlng.lat] });
      return;
    }
    setVertices((v) => [...v, latlng]);
  }

  function finishPolygon() {
    if (vertices.length < 3) return;
    const anillo: [number, number][] = vertices.map((v) => [v.lng, v.lat]);
    anillo.push(anillo[0]); // GeoJSON requires the ring to be closed.
    onCompletarDibujo?.({ type: "Polygon", coordinates: [anillo] });
    setVertices([]);
  }

  // Shift adds, Ctrl removes — never toggles: toggling was easy to trigger
  // by accident (the rectangle only had to graze a sub-basin that wasn't
  // meant to be touched) when trying to deselect just one in particular.
  function selectByRectangle(bounds: LatLngBounds, rectangleMode: RectangleSelectionMode) {
    if (!selectionMode) return;
    const copy = new Set(selectionMode.selection);
    for (const f of layer.features) {
      const limites = geometryBounds(f.geometry);
      if (!limites) continue;
      if (!bounds.intersects([limites[0], limites[1]])) continue;
      const subBasin = subBasinOf(project, f);
      if (!subBasin) continue;
      if (rectangleMode === "add") copy.add(subBasin.subBasinCode);
      else copy.delete(subBasin.subBasinCode);
    }
    selectionMode.onCambiarSeleccion(copy);
  }

  // Forces GeoJSON to rebuild popups/styles when a sub-basin's zone/model
  // changes (e.g. after editing from the edit panel), or one of the
  // special modes appears/disappears — react-leaflet doesn't re-run
  // onEachFeature/style just because props change. The selection's
  // CONTENT (`selectionMode.selection`) is left out on purpose: remounting
  // Turia's ~77 geometries on every selection click/rectangle was the
  // source of the noticeable lag when selecting — the effect below
  // (`subBasinsLayerRef.current.setStyle(...)`) takes care of repainting
  // just the style, without recreating the layer. onEachFeature's click
  // handler no longer depends on this chain either to know what's
  // selected — it reads `selectionRef` (see its comment).
  // Memoized: this is an O(sub-basins) string build (1728 for a real Duero
  // import) — SubBasinMap re-renders for plenty of reasons that don't touch
  // any of these deps (a sibling component's own state, App.tsx's other
  // fields...), and recomputing this on every one of those, on top of the
  // genuinely-needed recomputes while a bulk AEMET/ROCIO import is landing
  // sub-basin data every ~250ms (see APPLY_FLUSH_MS, aemetJobStore.ts),
  // was real measured jank (Chrome's own "long task" violations, reported
  // live from a Duero-scale project). Doesn't make the genuinely-needed
  // recomputes (subBasins actually changed) any cheaper — see this file's
  // sibling comment on `debouncedSignature` for that half of the fix.
  const signature = useMemo(
    () =>
      project.subBasins
        .map((c) => {
          const problems = problemsOf(c);
          return (
            `${c.gisCode}:${c.zoneCode}:${c.modelTypeCode}:${c.snowZoneCode}:${c.snowModelTypeCode}:` +
            `${c.outletCode}:${c.aquiferCode}:${problems.length}`
          );
        })
        .join("|") +
      "#" +
      project.zones.map((z) => `${z.zoneCode}:${z.name}`).join(",") +
      "#" +
      project.outletPoints.map((p) => `${p.outletCode}:${p.name}`).join(",") +
      "#" +
      project.aquifers.map((a) => `${a.aquiferCode}:${a.name}`).join(",") +
      "#" +
      (selectionMode ? "selection" : "no-selection") +
      "#" +
      (drawingMode ?? "no-drawing") +
      "#" +
      (resultsMode
        ? resultsMode.selection
          ? `${resultsMode.selection.level}:${resultsMode.selection.id}`
          : "results-mode-no-selection"
        : "no-results-mode") +
      "#" +
      (seriesMode
        ? seriesMode.selection
          ? `${seriesMode.selection.kind}:${seriesMode.selection.id}`
          : "series-mode-no-selection"
        : "no-series-mode") +
      "#" +
      (editingSubBasin ?? "not-editing") +
      "#" +
      (editingAquifer ?? "not-editing-aquifer") +
      "#" +
      (highlightedSubBasinCode ?? "not-highlighted") +
      "#" +
      (colorMode
        ? `color-${colorMode.field}:${colorMode.entities.map((e) => e.code).join(",")}`
        : "no-color"),
    [
      project.subBasins,
      project.zones,
      project.outletPoints,
      project.aquifers,
      // `problemsOf` (built fresh every render from `series`/`project`) reads
      // `series.precipitation`/`series.etp`, but only through `hasColumnAt`
      // (subBasinState.ts) — which only cares whether a column INDEX is
      // within bounds, i.e. `series.<kind>.columns.length`. Depending on the
      // whole `series` object used to invalidate this on every AEMET/ROCIO
      // apply that patches VALUES into an already-existing column (the
      // dominant case during a bulk import, landing every ~250ms — see
      // APPLY_FLUSH_MS, aemetJobStore.ts) even though `hasColumnAt`'s own
      // answer never actually changed. These two primitives change ONLY
      // when a column is genuinely added/removed — real gap found by
      // deep-review (2026-08-29).
      series.precipitation?.columns.length,
      series.etp?.columns.length,
      selectionMode,
      drawingMode,
      resultsMode,
      seriesMode,
      editingSubBasin,
      editingAquifer,
      highlightedSubBasinCode,
      colorMode,
    ]
  );

  // Debounces the REMOUNT itself (the style-only repaint below stays
  // instant, keyed off the live `signature`-derived state directly, not
  // this). Real incident (2026-08-25): a bulk MITECO+ROCIO import applies
  // one sub-basin's data at a time, and each one flips that sub-basin's
  // own "problems" (part of `signature`) as it arrives — for a
  // 1728-sub-basin import (Duero) that's up to 1728 full destroy+recreate
  // cycles of the ENTIRE layer, one per sub-basin, each rebuilding every
  // OTHER polygon/tooltip/click-handler too even though only one sub-basin
  // actually changed — real memory churn on top of everything else that
  // import already does. Waiting for SIGNATURE_DEBOUNCE_MS of silence
  // before remounting collapses a rapid burst into one remount at the end
  // instead of one per change; a manual single edit still remounts, just
  // after a barely-noticeable delay (the style-only effect below already
  // gives instant visual feedback in the meantime).
  const [debouncedSignature, setDebouncedSignature] = useState(signature);
  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedSignature(signature),
      SIGNATURE_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeout);
  }, [signature]);

  // Repaints the style of every sub-basin when the selection changes,
  // without remounting the layer (see the comment next to `signature`) —
  // Leaflet's `setStyle` only updates the existing SVG attributes, it
  // doesn't recreate the `<path>`s or call `onEachFeature` again (tooltips/
  // click handlers stay intact).
  useEffect(() => {
    subBasinsLayerRef.current?.setStyle((feature) => {
      const subBasin = subBasinOf(project, feature as unknown as SubBasinFeature);
      const state = visualStateOf(subBasin, problemsOf);
      const selected = !!(
        (subBasin && selectionMode?.selection.has(subBasin.subBasinCode)) ||
        (subBasin &&
          resultsMode &&
          sameSelection(resultsMode.selection, {
            level: "subBasin",
            id: subBasin.subBasinCode,
          })) ||
        (subBasin && subBasin.subBasinCode === editingSubBasin) ||
        (subBasin && subBasin.subBasinCode === highlightedSubBasinCode)
      );
      const colorFill =
        colorMode && colorMap && subBasin
          ? colorFor(colorMap, colorMode.field, subBasin)
          : undefined;
      return styleFor(state, selected, subBasin?.snowZoneCode != null, colorFill);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode?.selection]);

  // The geometry to pan to for `highlightedSubBasinCode` (SubBasinSearch.tsx)
  // — memoized so PanToSubBasin's own effect (keyed on this value's
  // identity) only re-fires when the highlight actually changes, not on
  // every unrelated re-render of this component.
  const highlightedBounds = useMemo(() => {
    if (highlightedSubBasinCode == null) return null;
    const feature = layer.features.find(
      (f) => subBasinOf(project, f)?.subBasinCode === highlightedSubBasinCode
    );
    return feature ? geometryBounds(feature.geometry) : null;
  }, [layer, project, highlightedSubBasinCode]);

  return (
    // Edit dialogs go OUTSIDE .mapa on purpose: that div has
    // `isolation: isolate` to contain Leaflet's internal z-indexes (up to
    // 700 in its panes) — a dialog nested inside would get trapped in that
    // same isolated layer and, with its z-index of 100, hidden behind the
    // map itself even though it was in the DOM (real bug found while
    // verifying this: the dialog opened but wasn't visible).
    <>
      <SnowPatterns />
      <div className="map">
        <MapContainer bounds={layerBounds(layer)} scrollWheelZoom boxZoom={false}>
          {/* key={language}: LayersControl is plain Leaflet under the hood (not React) — the
              text of its options is fixed at mount, it doesn't react to `name` prop changes
              afterward. Remounting it entirely on language change is cheap (3 layers) and
              is the only way for its labels to translate live. genericLayersSignature
              remounts it again whenever a generic layer is added/renamed/removed — see
              that variable's own comment. */}
          <LayersControl key={`${language}#${genericLayersSignature}`} position="topright">
            {BASEMAPS.map((basemap, i) => (
              <LayersControl.BaseLayer key={basemap.id} name={t(basemap.nameKey)} checked={i === 0}>
                <TileLayer
                  attribution={basemap.attribution}
                  url={basemap.url}
                  maxZoom={basemap.maxZoom}
                />
              </LayersControl.BaseLayer>
            ))}
            {genericLayersList.map((gl) => (
              <LayersControl.Overlay key={gl.id} name={gl.name} checked={gl.visible}>
                <GeoJSON
                  ref={(el) => {
                    if (el) genericLayerRefs.current.set(gl.id, el);
                    else genericLayerRefs.current.delete(gl.id);
                  }}
                  data={gl.geojson}
                  style={() => genericLayerPathOptions(gl.style)}
                  pointToLayer={(_feature, latlng) =>
                    circleMarker(latlng, genericLayerPathOptions(gl.style))
                  }
                >
                  <Tooltip sticky>{gl.name}</Tooltip>
                </GeoJSON>
              </LayersControl.Overlay>
            ))}
          </LayersControl>
          {onChangeGenericLayers && (
            <GenericLayersSync
              genericLayers={genericLayersList}
              onChangeGenericLayers={onChangeGenericLayers}
              layerRefs={genericLayerRefs}
            />
          )}
          {/* Not a LayersControl overlay (unlike before) — only rendered while
              SubBasinsPanel.tsx's "Add from MITECO" mode picked this one, see
              mitecoMode's own doc comment (mitecoLayers.ts). In "subBasins"
              mode the basin outlines are also shown underneath, read-only —
              so it's clear which basin (e.g. Júcar) a sub-basin belongs to
              without switching modes just to check (see
              MitecoBasinsOutline.tsx's own comment on why it's
              non-interactive). */}
          {mitecoMode?.layer === "basins" && <MitecoBasinsLayer mode={mitecoMode} />}
          {mitecoMode?.layer === "subBasins" && (
            <>
              <MitecoBasinsOutline />
              <MitecoSubBasinsLayer mode={mitecoMode} />
            </>
          )}
          <GeoJSON
            key={debouncedSignature}
            ref={subBasinsLayerRef}
            data={layer as unknown as GeoJSON.FeatureCollection}
            style={(feature) => {
              const subBasin = subBasinOf(project, feature as unknown as SubBasinFeature);
              const state = visualStateOf(subBasin, problemsOf);
              const selected = !!(
                (subBasin && selectionMode?.selection.has(subBasin.subBasinCode)) ||
                (subBasin &&
                  resultsMode &&
                  sameSelection(resultsMode.selection, {
                    level: "subBasin",
                    id: subBasin.subBasinCode,
                  })) ||
                (subBasin &&
                  seriesMode?.selection?.kind === "subBasin" &&
                  seriesMode.selection.id === subBasin.subBasinCode) ||
                (subBasin && subBasin.subBasinCode === editingSubBasin) ||
                (subBasin && subBasin.subBasinCode === highlightedSubBasinCode)
              );
              const colorFill =
                colorMode && colorMap && subBasin
                  ? colorFor(colorMap, colorMode.field, subBasin)
                  : undefined;
              return styleFor(state, selected, subBasin?.snowZoneCode != null, colorFill);
            }}
            onEachFeature={
              onEachFeature({
                t,
                project,
                problemsOf,
                selectionMode,
                selectionRef,
                isSelectedRef,
                drawingMode,
                resultsMode,
                seriesMode,
                onEditSubBasin: onChangeProject
                  ? (c) => setEditingSubBasin(c.subBasinCode)
                  : undefined,
                colorMode: colorMode ?? undefined,
                colorMap,
              }) as (feature: GeoJSON.Feature, layer: Layer) => void
            }
          />
          {aquifersLayer && aquifersLayer.features.length > 0 && (
            <GeoJSON
              key={`acu-${debouncedSignature}-${aquifersLayer.features.length}`}
              data={aquifersLayer as unknown as GeoJSON.FeatureCollection}
              style={(feature) => {
                const aquifer = aquiferOf(project, feature as unknown as AquiferFeature);
                const selected =
                  !!aquifer &&
                  (aquifer.aquiferCode === editingAquifer ||
                    (seriesMode?.selection?.kind === "aquifer" &&
                      seriesMode.selection.id === aquifer.aquiferCode) ||
                    (!!resultsMode &&
                      sameSelection(resultsMode.selection, {
                        level: "aquifer",
                        id: aquifer.aquiferCode,
                      })));
                return selected ? { ...AQUIFER_STYLE, ...SELECTION_HIGHLIGHT } : AQUIFER_STYLE;
              }}
              onEachFeature={(feature, layer) => {
                const aquifer = aquiferOf(project, feature as unknown as AquiferFeature);
                layer.bindTooltip(escapeHtml(aquifer?.name ?? t("legend.aquifer")), {
                  sticky: true,
                  direction: "top",
                });
                if (drawingMode) {
                  // Same as sub-basins: in drawing mode, clicking doesn't
                  // select or edit.
                } else if (resultsMode) {
                  // In results mode, clicking selects that aquifer in the
                  // Results panel — same as sub-basins/outlet points.
                  layer.on("click", () => {
                    if (aquifer)
                      resultsMode.onSelect({ level: "aquifer", id: aquifer.aquiferCode });
                  });
                } else if (seriesMode) {
                  // "Input series" tab open: clicking filters the pumping
                  // chart to this aquifer instead of opening its dialog.
                  layer.on("click", () => {
                    if (aquifer) seriesMode.onSelect({ kind: "aquifer", id: aquifer.aquiferCode });
                  });
                } else if (onChangeProject) {
                  // Outside any special mode, clicking opens its edit
                  // dialog directly. It doesn't take part in multi-selection
                  // (selectionMode is sub-basins only).
                  layer.on("click", () => {
                    if (aquifer) setEditingAquifer(aquifer.aquiferCode);
                  });
                }
              }}
            />
          )}
          {outletPointsLayer?.features.map((f, i) => {
            if (f.geometry.type !== "Point") return null;
            const [lng, lat] = f.geometry.coordinates;
            // Resolved against the live project.outletPoints, not the
            // snapshot embedded in the layer when it was created — same
            // reason as subBasinOf.
            const outlet = f.properties.outletPoint
              ? (project.outletPoints.find(
                  (p) => p.outletCode === f.properties.outletPoint!.outletCode
                ) ?? f.properties.outletPoint)
              : null;
            const name = outlet?.name ?? t("legend.outletPoint");
            const selected =
              !!outlet &&
              ((!!resultsMode &&
                sameSelection(resultsMode.selection, {
                  level: "outletPoint",
                  id: outlet.outletCode,
                })) ||
                outlet.outletCode === editingOutlet);
            const onClick = drawingMode
              ? undefined
              : resultsMode && outlet
                ? () =>
                    resultsMode.onSelect({
                      level: "outletPoint",
                      id: outlet.outletCode,
                    })
                : onChangeProject && outlet
                  ? () => setEditingOutlet(outlet.outletCode)
                  : undefined;
            return (
              <CircleMarker
                key={outlet?.outletCode ?? String(f.properties.idGis ?? i)}
                center={[lat, lng]}
                radius={selected ? 9 : 6}
                pathOptions={
                  selected ? { ...OUTLET_POINT_STYLE, ...SELECTION_HIGHLIGHT } : OUTLET_POINT_STYLE
                }
                eventHandlers={onClick ? { click: onClick } : undefined}
              >
                <Tooltip direction="top">{name}</Tooltip>
              </CircleMarker>
            );
          })}
          {selectionMode && !drawingMode && (
            <RectangleSelectionLayer onSelect={selectByRectangle} />
          )}
          {drawingMode && (
            <>
              <DrawEvents onMapClick={onDrawMapClick} />
              {!isPointTool(drawingMode) && <VistaPreviaDibujo vertices={vertices} />}
            </>
          )}
          <AemetStationsLayer />
          <TooltipVisibilityFix remountKey={debouncedSignature} />
          <PanToSubBasin bounds={highlightedBounds} />
        </MapContainer>

        <Legend />
        <GenericLayersLegend layers={genericLayersList} />

        {drawingMode && (
          <div className="map-drawing-bar">
            {isPointTool(drawingMode) ? (
              <span>{t("map.clickToPlace", { tool: t(TOOL_LABEL[drawingMode]) })}</span>
            ) : (
              <>
                <span>
                  {t("map.vertexCount", {
                    n: vertices.length,
                    word: t(vertices.length === 1 ? "map.vertexSingular" : "map.vertexPlural"),
                  })}
                </span>
                <button
                  className="button button-primary"
                  disabled={vertices.length < 3}
                  onClick={finishPolygon}
                >
                  {t("map.finish")}
                </button>
              </>
            )}
            <button
              className="button"
              onClick={() => {
                setVertices([]);
                onCancelDrawing?.();
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        )}
      </div>

      {/*
       * Sibling of .map, not nested inside it, same reasoning as
       * EntityEditDialogs below: .map sets `isolation: isolate` (to keep
       * Leaflet's own panes, up to z-index 700, from leaking past it) —
       * that traps any z-index set on a DESCENDANT too, including this
       * legend's own 1000, capping it below .results-panel (z-index 25)
       * whenever both are visible at once (the Results panel's "Outlet
       * point"/"Zone" level, see deriveMapModes.ts). Rendering it as a
       * sibling instead lets its z-index compare directly against
       * .results-panel's in .workspace's own stacking context, where 1000
       * actually wins.
       */}
      {colorMode && (
        <AssignmentColorLegend
          colorMode={colorMode}
          besideResultsPanel={!!resultsMode}
          aboveEphemeralBanner={!!resultsMode && !!isEphemeral}
        />
      )}

      <EntityEditDialogs
        project={project}
        series={series}
        gisLayer={layer}
        aemetProvenance={aemetProvenance}
        onRecordAemetProvenance={onRecordAemetProvenance}
        onChangeProject={onChangeProject}
        onChangeSeries={onChangeSeries}
        editingSubBasinObj={editingSubBasinObj}
        editingOutletObj={editingOutletObj}
        editingAquiferObj={editingAquiferObj}
        onCloseSubBasin={() => setEditingSubBasin(null)}
        onCloseOutlet={() => setEditingOutlet(null)}
        onCloseAquifer={() => setEditingAquifer(null)}
        observedFlow={observedFlow}
        onChangeObservedFlow={onChangeObservedFlow}
        calibrationMode={calibrationMode}
      />
    </>
  );
}
